import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux"
import PropTypes from "prop-types"
import { Row, Col, Button, Input, Tag, message } from "antd"
import axios from "axios"
import isEmail from 'validator/lib/isEmail'

import { pageChanged, startPageLoading, stopPageLoading } from "../../../../data/redux/page_details/actions"
import { getIsMobile } from "../../../../data/redux/page_details/selectors"
import { APP_PAGES, METHOD_TYPES } from "../../../../data/config/constants"
import { log } from "../../../../data/config/utils"

import InputItem from "../../../../components/form/inputitem"
import TextareaItem from "../../../../components/form/textareaitem"

const initialFormState = {
  to: "customercare@vi.com",
  from: "",
  subject: "",
  content: ""
}

const MAX_RETRY_COUNT = 20
const WAITING_TIME = 5000

const EmailSendBox = ({
  is_mobile,
  actions
}) => {
  const [formData, setFormData] = useState(initialFormState)
  const [response, setResponse] = useState({})
  const { pageChanged, startPageLoading, stopPageLoading } = actions
  const timerRef = useRef(null)
  const retryRef = useRef(0)

  useEffect(() => {
    pageChanged(APP_PAGES.EMAIL_SEND_BOX, APP_PAGES.EMAIL_SEND_BOX)
  }, [])

  const handleFormChange = e => {
    const key = e.target.name
    const value = e.target.value
    setFormData(prevState => ({
      ...prevState,
      [key]: value
    }))
  }

  const fetchDataAndProceed = (
    {
      url,
      baseURL = "https://vil-email-dev.oriserve.com",
      method = METHOD_TYPES.GET,
      data = {},
      timeout = 30000
    } = {},
    callback
  ) => {
    axios({
      url,
      baseURL,
      method,
      timeout,
      params: method === METHOD_TYPES.GET ? data : {},
      data: method !== METHOD_TYPES.GET ? data : {},
      headers: { "Content-Type": "application/json" },
      validateStatus: status => ((status >= 200 && status < 300) || status === 412),
    }).then(response => {
      callback(false, response.data)
    }).catch(error => {
      callback(true)
      if (error && error.response) {
        if (error.response.data && error.response.data.message)
          message.error(error.response.data.message)
        else if (error.response.message)
          message.error(error.response.message)
      }
    })
  }

  const fetchTemplate = data => {
    fetchDataAndProceed({
      url: "/getEmailData",
      data
    }, (err, res) => {
      log("fetchTemplate", err, res)
      stopPageLoading()
      if (!err && res && res.data) {
        if (res.data.processingStatus === "queued" && retryRef.current < MAX_RETRY_COUNT) {
          retryRef.current = retryRef.current + 1
          startPageLoading(`Retry attempt${retryRef.current}: Retring template loading`)
          if (timerRef.current)
            clearTimeout(timerRef.current)
          timerRef.current = setTimeout(() => fetchTemplate(data), WAITING_TIME)
        } else {
          retryRef.current = 0
          clearFormData()
          message.success("Template loaded successfully")
          setResponse({
            responseMode: res.data.responseMode,
            processingStatus: res.data.processingStatus,
            endTemplate: res.data.endTemplate
          })
        }
      } else {
        message.success("Something went wrong")
      }
    })
  }

  const handleSendEmail = () => {
    if (formData.from && formData.subject && formData.content) {
      if (isEmail(formData.from)) {
        startPageLoading("Sending")
        setResponse({})
        fetchDataAndProceed({
          url: "/processEmail",
          method: METHOD_TYPES.POST,
          data: formData
        }, (err, res) => {
          log("handleSendEmail", err, res)
          if (!err && res && res.data && res.data.trackingId) {
            message.success("Email sent successfully")
            startPageLoading("Template loading")
            if (timerRef.current)
              clearTimeout(timerRef.current)
            timerRef.current = setTimeout(() => fetchTemplate({ trackId: res.data.trackingId }), WAITING_TIME)
          } else
            stopPageLoading()
        })
      } else
        message.warning("Please Enter a valid email address")
    } else
      message.warning("All filelds are require")
  }

  const clearFormData = () => setFormData(initialFormState)

  return (
    <div className={`ori-bg-white ${is_mobile ? "" : "ori-full-parent-height ori-flex-column"}`}>
      <Row className={`${is_mobile ? "" : "ori-full-flex ori-overflow-auto"}`}>
        <Col
          xs={24}
          md={12}
          className={`ori-pad-15 ${is_mobile ? "ori-b-border-light" : "ori-r-border-light ori-full-parent-height ori-overflow-auto"}`}
        >
          <div className="ori-b-pad-15 ori-b-mrgn-10 ori-b-border-light">
            Customer Mail
          </div>
          <InputItem
            title="To"
            type="email"
            placeholder="Enter email"
            name="to"
            value={formData.to}
            onChange={handleFormChange}
            disabled
          />
          <InputItem
            title="From"
            type="email"
            placeholder="Enter email"
            name="from"
            value={formData.from}
            onChange={handleFormChange}
          />
          <InputItem
            title="Subject"
            placeholder="Enter subject"
            name="subject"
            value={formData.subject}
            onChange={handleFormChange}
          />
          <TextareaItem
            title="Body"
            placeholder="Enter content here..."
            min_rows={10}
            max_rows={16}
            name="content"
            value={formData.content}
            onChange={handleFormChange}
          />
          <div className="ori-t-mrgn-20 ori-b-mrgn-10 ori-flex-row ori-flex-jc">
            <Button
              className="ori-r-mrgn-15"
              onClick={clearFormData}
            >
              Clear
            </Button>
            <Button
              type="primary"
              onClick={handleSendEmail}
            >
              Send
            </Button>
          </div>
        </Col>
        <Col
          xs={24}
          md={12}
          className={`ori-pad-15 ${is_mobile ? "" : "ori-full-parent-height ori-overflow-auto"}`}
        >
          <div className="ori-b-pad-15 ori-b-mrgn-10 ori-b-border-light">
            <span className="ori-r-mrgn-15">Bot Response</span>
            {
              response.responseMode &&
              <Tag>{response.responseMode}</Tag>
            }
            {
              response.processingStatus &&
              <Tag>{response.processingStatus}</Tag>
            }
          </div>
          {
            response.endTemplate &&
            <Input.TextArea
              className="ori-font-default"
              value={response.endTemplate}
              autosize
              disabled
            />
          }
        </Col>
      </Row >
    </div >
  )
}

const mapStateToProps = state => {
  return {
    is_mobile: getIsMobile(state),
  }
}

const mapDispatchToProps = dispatch => {
  return {
    actions: bindActionCreators({
      pageChanged,
      startPageLoading,
      stopPageLoading
    }, dispatch)
  }
}

EmailSendBox.propTypes = {
  actions: PropTypes.object,
  is_mobile: PropTypes.bool
}

export default connect(mapStateToProps, mapDispatchToProps)(EmailSendBox);


// WEBPACK FOOTER //
// ./src/app/pagecontainer/modules/emailsendbox/index.js